import { ringDistance, tileDistance } from '../world/grid.js';
import { canStrike } from './ranged.js';
/** Nearest player within `range`, ties broken by iteration order (stable). */
export function nearestPlayerWithin(position, players, range) {
    let best = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const [id, tile] of players) {
        const distance = tileDistance(position, tile);
        if (distance <= range && distance < bestDistance) {
            best = id;
            bestDistance = distance;
        }
    }
    return best;
}
const everywhere = () => true;
export function decideMob(state, situation, definition) {
    const { position, home, players } = situation;
    const open = situation.open ?? everywhere;
    // A biter stops next to its prey, a shooter as soon as it has it in its sights.
    const inReach = (target) => canStrike(definition, position, target, open);
    if (state.mood === 'chase' && state.targetId !== null) {
        const target = players.get(state.targetId);
        const tooFarFromHome = tileDistance(position, home) > definition.leashRange;
        // The chase ends when the target is gone (left the zone or the area of
        // interest) or has clearly outrun the mob: twice the aggro range.
        const lost = target === undefined || tileDistance(position, target) > definition.aggroRange * 2;
        // A boss whose prey fell turns on the next one in range: walking home
        // heals it whole, and a raid must not start over at every death.
        const next = lost && !tooFarFromHome && definition.rank !== 'ordinary' && situation.passive !== true
            ? nearestPlayerWithin(position, players, definition.aggroRange)
            : null;
        if (next !== null) {
            const prey = players.get(next);
            const destination = prey !== undefined && !inReach(prey) ? prey : null;
            return { mood: 'chase', targetId: next, destination };
        }
        if (tooFarFromHome || lost) {
            return { mood: 'return', targetId: null, destination: home };
        }
        const destination = inReach(target) ? null : target;
        return { mood: 'chase', targetId: state.targetId, destination };
    }
    if (state.mood === 'return') {
        if (tileDistance(position, home) === 0)
            return { mood: 'idle', targetId: null, destination: null };
        // A returning mob is not distracted: it must reach home before aggroing
        // again, or a player could drag it across the whole zone.
        return { mood: 'return', targetId: null, destination: home };
    }
    const prey = definition.peaceful || situation.passive === true
        ? null
        : nearestPlayerWithin(position, players, definition.aggroRange);
    if (prey !== null) {
        const target = players.get(prey);
        const destination = target !== undefined && !inReach(target) ? target : null;
        return { mood: 'chase', targetId: prey, destination };
    }
    // Nothing to hunt: a short stroll near home, so the valley is never frozen.
    return { mood: 'idle', targetId: null, destination: situation.stroll ?? null };
}
/** How far from its prey a mob may wait when every tile in reach is taken. */
const WAITING_RING = 2;
/**
 * The tile a chasing mob heads for: a free one around its prey, so a pack
 * spreads over the eight neighbours instead of piling onto one. The ring in
 * reach comes first, then a waiting ring just outside it; within a ring, the
 * tile nearest the mob, so each one takes the side it arrives from. A mob
 * already in reach keeps its tile. Ties fall to a fixed order, so the choice
 * is the same on every think and the mob does not dither.
 *
 * `isFree` says whether the mob may end there: walkable, and neither stood on
 * nor claimed by anyone else. Null when the whole neighbourhood is taken.
 */
export function engageSlot(position, target, isFree) {
    let best = null;
    let bestRing = Number.POSITIVE_INFINITY;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let dy = -WAITING_RING; dy <= WAITING_RING; dy += 1) {
        for (let dx = -WAITING_RING; dx <= WAITING_RING; dx += 1) {
            if (dx === 0 && dy === 0)
                continue;
            const tile = { x: target.x + dx, y: target.y + dy };
            const ring = ringDistance(tile, target);
            const distance = tileDistance(position, tile);
            if (ring > bestRing || (ring === bestRing && distance >= bestDistance))
                continue;
            if (!isFree(tile))
                continue;
            best = tile;
            bestRing = ring;
            bestDistance = distance;
        }
    }
    return best;
}
/*
 * The creatures' ways in a fight (`behaviours` in `data/mobs.ts`). The room
 * keeps the clocks and the tiles; these say where to go and who answers.
 */
/** Under this share of its health, a creature that flees backs away. */
export const FLEE_HEALTH_SHARE = 0.2;
/** How long it backs away before it turns and fights again. */
export const FLEE_MS = 3000;
/** Its pace while it backs away, this many times slower than its walk: a player always catches it. */
export const FLEE_SLOWDOWN = 1.5;
/** Farthest it backs away, in rings from where it stood. */
export const FLEE_RINGS = 4;
/** Kin this close (in rings) to a struck creature that calls for help join the fight. */
export const CALL_FOR_HELP_RINGS = 4;
/** Farthest a patrol's corners lie from home, whatever its wander radius. */
export const PATROL_RADIUS = 3;
/** Whether a creature this hurt backs away: strictly under a fifth of its health, and alive. */
export function shouldFlee(hp, maxHp) {
    return hp > 0 && hp < maxHp * FLEE_HEALTH_SHARE;
}
/** Its walk while it backs away, in ms per tile. */
export function fleePace(msPerTile) {
    return Math.round(msPerTile * FLEE_SLOWDOWN);
}
/**
 * Where a hurt creature backs away to: the tile within `FLEE_RINGS` that lies
 * farthest from its threat, never beyond its leash (`tileDistance` to home at
 * most `leashRange`), so it never runs off with the fight. Ties go to the
 * tile nearer home, then to a fixed order. Null when no tile is farther from
 * the threat than where it stands: it fights on.
 */
export function fleeTile(position, threat, home, leashRange, isFree) {
    let best = null;
    let bestGap = tileDistance(position, threat);
    let bestHome = Number.POSITIVE_INFINITY;
    for (let dy = -FLEE_RINGS; dy <= FLEE_RINGS; dy += 1) {
        for (let dx = -FLEE_RINGS; dx <= FLEE_RINGS; dx += 1) {
            const tile = { x: position.x + dx, y: position.y + dy };
            const toHome = tileDistance(tile, home);
            if (toHome > leashRange)
                continue;
            const gap = tileDistance(tile, threat);
            if (gap < bestGap || (gap === bestGap && (best === null || toHome >= bestHome)))
                continue;
            if (!isFree(tile))
                continue;
            best = tile;
            bestGap = gap;
            bestHome = toHome;
        }
    }
    return best;
}
/**
 * A patrol's circuit: the four corners of a square round home, walked in
 * order. Its half side is the creature's wander radius, `PATROL_RADIUS` at
 * most; empty for one that stands still.
 */
export function patrolCircuit(home, wanderRadius) {
    const r = Math.min(wanderRadius, PATROL_RADIUS);
    if (r <= 0)
        return [];
    return [
        { x: home.x - r, y: home.y - r },
        { x: home.x + r, y: home.y - r },
        { x: home.x + r, y: home.y + r },
        { x: home.x - r, y: home.y + r },
    ];
}
/**
 * The next corner to walk to from `index` (wrapping round), skipping those it
 * may not stand on (a wall, a tree, another creature). Null when none is free.
 */
export function nextPatrolStop(circuit, index, isFree) {
    for (let offset = 0; offset < circuit.length; offset += 1) {
        const at = (((index + offset) % circuit.length) + circuit.length) % circuit.length;
        const tile = circuit[at];
        if (tile !== undefined && isFree(tile))
            return { tile, index: at };
    }
    return null;
}
/**
 * Whether a creature answers its kin's call for help: the same kind, close
 * enough, and at rest. One already fighting or walking home goes on with it.
 */
export function answersCallForHelp(caller, helper) {
    return (helper.mobId === caller.mobId &&
        helper.mood === 'idle' &&
        ringDistance(helper.position, caller.position) <= CALL_FOR_HELP_RINGS);
}
//# sourceMappingURL=mob-ai.js.map